import * as Tone from 'tone';
import { GrainLayer } from './grain-layer';
import { Effects } from './effects';
import { Transport } from './transport';
import { AudioRecorder, type RecordingTarget } from './recorder';
import { MarkovMelody } from '../generators/markov';
import { moodToScale } from '../generators/scales';
import type { LayerType, GenerationParams } from '../types';
import type { ScaleName } from '../generators/scales';

/**
 * AudioEngine - Singleton class managing all audio functionality
 * Handles sample loading, 4-layer grain synthesis, timing, melody/harmony generation, and master effects
 */
export class AudioEngine {
  private static instance: AudioEngine;

  private buffer: Tone.ToneAudioBuffer | null = null;
  private layers: Map<LayerType, GrainLayer> = new Map();
  private effects: Effects;
  private transport: Transport;
  private recorder: AudioRecorder;
  private markovMelody: MarkovMelody;
  private isPlaying = false;

  // Musical parameters
  private rootNote = 60; // C4 (middle C)
  private currentScale: ScaleName = 'major';
  
  // More varied bass patterns (intervals from root)
  private bassPatterns = [
    [0, 0, 7, 5],           // Root, root, fifth, fourth
    [0, 7, 5, 7],           // Root, fifth, fourth, fifth
    [0, 5, 7, 0],           // Root, fourth, fifth, root
    [0, 0, 0, 7],           // Mostly root with fifth
    [0, 3, 5, 7],           // Walking pattern
    [0, 7, 0, 5],           // Alternating
    [0, 0, 5, 0],           // Root-focused with fourth
    [0, 7, 7, 0],           // Root and fifth emphasis
  ];
  
  // More varied chord voicings (intervals from root)
  private padChords = [
    [0, 4, 7],              // Major triad
    [0, 3, 7],              // Minor triad
    [0, 4, 7, 11],          // Major 7th
    [0, 3, 7, 10],          // Minor 7th
    [0, 5, 7],              // Sus4
    [0, 2, 7],              // Sus2
    [0, 4, 7, 9],           // Add6
    [0, 4, 7, 14],          // Add9
    [0, 7, 12],             // Power chord + octave
    [0, 4, 11],             // Major 7 no 5
  ];
  
  private currentBassPattern: number[] = [0, 0, 7, 5];
  private currentPadChord: number[] = [0, 4, 7];

  private constructor() {
    // Create master effects chain
    this.effects = new Effects();

    // Create transport (timing engine)
    this.transport = new Transport(120); // Default 120 BPM

    // Create audio recorder
    this.recorder = new AudioRecorder();

    // Create Markov melody generator
    this.markovMelody = new MarkovMelody(this.rootNote, this.currentScale, 1);

    // Create 4 grain layers
    this.createLayers();

    // Setup recording after layers are created
    this.setupRecording();

    // Listen for transport step events
    this.setupTransportListener();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  /**
   * Create 4 independent grain layers
   */
  private createLayers(): void {
    const layerTypes: LayerType[] = ['percussion', 'bass', 'melody', 'pad'];

    layerTypes.forEach((type) => {
      const layer = new GrainLayer(type, this.effects.input);
      this.layers.set(type, layer);
    });
  }

  /**
   * Setup recording destinations for main output and all layers
   */
  private setupRecording(): void {
    const layerMap = new Map<LayerType, { getGainNode: () => Tone.Gain }>();
    this.layers.forEach((layer, type) => {
      layerMap.set(type, { getGainNode: () => layer.getGainNode() });
    });
    
    this.recorder.setupLayerRecording(layerMap, this.effects.getMasterOutput());
  }

  /**
   * Load audio sample from file
   */
  async loadSample(file: File): Promise<void> {
    try {
      // Convert file to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Create blob URL and load into Tone.ToneAudioBuffer
      const blob = new Blob([arrayBuffer], { type: file.type });
      const url = URL.createObjectURL(blob);

      this.buffer = await Tone.ToneAudioBuffer.fromUrl(url);

      // Clean up blob URL
      URL.revokeObjectURL(url);

      // Set buffer for all layers
      this.layers.forEach((layer) => {
        layer.setBuffer(this.buffer!);
      });
    } catch (error) {
      console.error('❌ Error loading sample:', error);
      throw error;
    }
  }

  /**
   * Setup transport step listener
   * Triggers layers on beat according to their patterns with intelligent pitch selection
   */
  private setupTransportListener(): void {
    window.addEventListener('transport:step', ((event: CustomEvent) => {
      const { time, step, patterns } = event.detail;

      // Check each layer's pattern and trigger if it's a hit
      this.layers.forEach((layer, type) => {
        const pattern = patterns.get(type);
        if (pattern && pattern[step]) {
          // Get pitch for this layer
          const pitch = this.getPitchForLayer(type, step);

          // Trigger grain with pitch
          layer.trigger(time, pitch);
        }
      });
    }) as EventListener);
  }

  /**
   * Get pitch for specific layer
   * @param type - Layer type
   * @param step - Current step (0-15)
   * @returns Pitch offset in semitones (0 = original pitch)
   */
  private getPitchForLayer(type: LayerType, step: number): number {
    switch (type) {
      case 'percussion':
        // Percussion: more varied pitch for texture (-12 to +12 range)
        if (Math.random() > 0.5) {
          // Frequent pitch shifts across 2 octaves
          const baseShift = (Math.random() - 0.5) * 12;
          // Occasional octave jump
          const octave = Math.random() > 0.8 ? (Math.random() > 0.5 ? 12 : -12) : 0;
          return baseShift + octave;
        }
        return (Math.random() - 0.5) * 4;

      case 'bass':
        // Bass: follow current bass pattern, stays low (-24 to -12 range)
        const bassIndex = step % this.currentBassPattern.length;
        const bassInterval = this.currentBassPattern[bassIndex];
        // Occasional octave jump for variety
        const bassOctaveShift = Math.random() > 0.85 ? (Math.random() > 0.5 ? 12 : -12) : 0;
        return bassInterval - 24 + bassOctaveShift; // Two octaves down base

      case 'melody':
        // Melody: Markov chain generated notes with wider octave range
        const melodyNote = this.markovMelody.nextNote();
        const melodyBase = melodyNote - this.rootNote;
        // Add octave variety: can go up 1-2 octaves or stay in place
        const melodyOctave = Math.random() > 0.6 
          ? (Math.random() > 0.5 ? 12 : 24) 
          : (Math.random() > 0.8 ? -12 : 0);
        return melodyBase + melodyOctave;

      case 'pad':
        // Pad: chord tones spread across octaves for richer sound
        const chordTone = this.currentPadChord[step % this.currentPadChord.length];
        // Spread pads across -12 to +12 range
        const padOctave = Math.random() > 0.4 
          ? (Math.random() > 0.5 ? 12 : -12)
          : 0;
        // Occasional passing tone
        const passing = Math.random() > 0.85 ? (Math.random() > 0.5 ? 1 : -1) : 0;
        return chordTone + padOctave + passing;

      default:
        return 0;
    }
  }
  
  /**
   * Randomize musical patterns (called on randomize)
   */
  private randomizeMusicalPatterns(): void {
    // Pick random bass pattern
    this.currentBassPattern = this.bassPatterns[
      Math.floor(Math.random() * this.bassPatterns.length)
    ];
    
    // Pick random pad chord based on scale/mood
    // Filter chords to those that fit the current scale better
    const majorChords = [0, 3, 5, 6, 7, 8]; // Indices of more major-sounding chords
    const minorChords = [1, 3, 4, 5, 9];    // Indices of more minor-sounding chords
    
    const chordPool = this.currentScale.includes('minor') || 
                      this.currentScale.includes('phrygian') ||
                      this.currentScale.includes('locrian') ||
                      this.currentScale === 'dorian'
      ? minorChords 
      : majorChords;
    
    const chordIndex = chordPool[Math.floor(Math.random() * chordPool.length)];
    this.currentPadChord = this.padChords[chordIndex];
    
    // Randomize melody contour
    this.markovMelody.randomizeContour();
  }

  /**
   * Start playback (transport + all layers)
   */
  async start(): Promise<void> {
    if (!this.buffer) {
      console.error('❌ No buffer loaded');
      return;
    }

    try {
      // Start Tone.js audio context (required for first interaction)
      await Tone.start();

      // Start transport (begins sequencing)
      this.transport.start();

      this.isPlaying = true;
    } catch (error) {
      console.error('❌ Error starting playback:', error);
      throw error;
    }
  }

  /**
   * Stop playback (transport + all layers)
   */
  stop(): void {
    if (this.isPlaying) {
      this.transport.stop();

      this.layers.forEach((layer) => {
        layer.stop();
      });

      this.isPlaying = false;
    }
  }

  /**
   * Update generation parameters for all layers
   */
  updateParams(params: GenerationParams): void {
    this.layers.forEach((layer) => {
      layer.updateFromParams(params);
    });

    // Update transport BPM from tempo param
    this.transport.setBPM(params.tempo);

    // Update scale from mood param
    if (params.mood !== undefined) {
      const newScale = moodToScale(params.mood);
      if (newScale !== this.currentScale) {
        this.currentScale = newScale;
        this.markovMelody.changeScale(this.rootNote, this.currentScale);
        this.randomizeMusicalPatterns(); // Update patterns for new scale
      }
    }

    // Density affects pattern density (how many hits per measure)
    // This is the primary control for how many triggers per measure
    if (params.density !== undefined) {
      this.transport.setDensity(params.density);
    }
    
    // Variation triggers full randomization
    if (params.variation !== undefined && params.variation > 0) {
      this.transport.regeneratePatterns(params.variation);
      this.randomizeMusicalPatterns();
    }
  }
  
  /**
   * Full randomization of all musical elements
   */
  randomize(): void {
    this.randomizeMusicalPatterns();
    this.transport.regeneratePatterns(Math.random() * 100);
  }

  /**
   * Set root note (key)
   */
  setRootNote(midiNote: number): void {
    this.rootNote = midiNote;
    this.markovMelody.changeScale(this.rootNote, this.currentScale);
  }

  /**
   * Set scale
   */
  setScale(scale: ScaleName): void {
    this.currentScale = scale;
    this.markovMelody.changeScale(this.rootNote, this.currentScale);
  }

  /**
   * Set BPM
   */
  setBPM(bpm: number): void {
    this.transport.setBPM(bpm);
  }

  /**
   * Get current BPM
   */
  getBPM(): number {
    return this.transport.getBPM();
  }

  /**
   * Get current root note (MIDI number)
   */
  getRootNote(): number {
    return this.rootNote;
  }

  /**
   * Get current scale name
   */
  getScale(): ScaleName {
    return this.currentScale;
  }

  /**
   * Set volume for specific layer
   */
  setLayerVolume(type: LayerType, volume: number): void {
    const layer = this.layers.get(type);
    if (layer) {
      layer.setVolume(volume);
    }
  }

  /**
   * Set reverb amount
   */
  setReverbAmount(amount: number): void {
    this.effects.setReverbAmount(amount);
  }

  /**
   * Set delay amount
   */
  setDelayAmount(amount: number): void {
    this.effects.setDelayAmount(amount);
  }

  /**
   * Set compression amount (0-1)
   */
  setCompressionAmount(amount: number): void {
    this.effects.setCompressionAmount(amount);
  }

  /**
   * Set saturation amount (0-1)
   */
  setSaturationAmount(amount: number): void {
    this.effects.setSaturationAmount(amount);
  }

  /**
   * Set global smoothness (adjusts ADSR attack/release for all layers)
   * @param amount - 0 (clicky/punchy) to 100 (smooth/ambient)
   */
  setSmoothness(amount: number): void {
    const normalized = amount / 100; // 0-1
    
    // Base ADSR multipliers based on smoothness
    // At 0: quick attacks, short releases (punchy, potentially clicky)
    // At 100: slow attacks, long releases (smooth, ambient)
    const attackMultiplier = 0.5 + normalized * 4; // 0.5x to 4.5x
    const releaseMultiplier = 0.5 + normalized * 3; // 0.5x to 3.5x
    
    this.layers.forEach((layer, type) => {
      // Get base ADSR values for each layer type
      const baseADSR = this.getBaseADSR(type);
      
      layer.setADSR({
        attack: baseADSR.attack * attackMultiplier,
        release: baseADSR.release * releaseMultiplier,
      });
    });
  }
  
  /**
   * Set live variation amount (how much patterns evolve during playback)
   * @param amount - 0 (static) to 100 (maximum evolution)
   */
  setLiveVariation(amount: number): void {
    this.transport.setLiveVariation(amount);
  }
  
  /**
   * Get current live variation amount
   */
  getLiveVariation(): number {
    return this.transport.getLiveVariation();
  }
  
  /**
   * Lock/unlock patterns (when locked, patterns never change during playback)
   */
  setPatternLock(locked: boolean): void {
    this.transport.setPatternLock(locked);
  }
  
  /**
   * Check if patterns are locked
   */
  isPatternsLocked(): boolean {
    return this.transport.isPatternsLocked();
  }

  /**
   * Get base ADSR values for layer type (used for smoothness scaling)
   */
  private getBaseADSR(type: LayerType): { attack: number; release: number } {
    switch (type) {
      case 'percussion':
        return { attack: 0.002, release: 0.02 };
      case 'bass':
        return { attack: 0.008, release: 0.05 };
      case 'melody':
        return { attack: 0.005, release: 0.04 };
      case 'pad':
        return { attack: 0.02, release: 0.1 };
    }
  }

  /**
   * Get playback state
   */
  getIsPlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Get buffer duration
   */
  getBufferDuration(): number {
    return this.buffer?.duration ?? 0;
  }

  /**
   * Check if sample is loaded
   */
  hasBuffer(): boolean {
    return this.buffer !== null;
  }

  /**
   * Get buffer for visualization
   */
  getBuffer(): Tone.ToneAudioBuffer | null {
    return this.buffer;
  }

  /**
   * Get specific layer
   */
  getLayer(type: LayerType): GrainLayer | undefined {
    return this.layers.get(type);
  }

  // ============= RECORDING METHODS =============

  /**
   * Start recording main output and all layers
   */
  startRecording(): void {
    this.recorder.startRecording();
  }

  /**
   * Stop recording and return all recordings
   */
  async stopRecording(): Promise<Map<RecordingTarget, Blob>> {
    return this.recorder.stopRecording();
  }

  /**
   * Check if currently recording
   */
  getIsRecording(): boolean {
    return this.recorder.getIsRecording();
  }

  /**
   * Get current recording duration
   */
  getRecordingDuration(): number {
    return this.recorder.getRecordingDuration();
  }

  /**
   * Download all recordings
   */
  async downloadRecordings(recordings: Map<RecordingTarget, Blob>, baseName: string): Promise<void> {
    await this.recorder.downloadAllRecordings(recordings, baseName);
  }

  /**
   * Get recorder instance for advanced control
   */
  getRecorder(): AudioRecorder {
    return this.recorder;
  }
}